export interface PListDto {
  id: number;
  companyNo: string;
  pListID: number;
  description1?: string;
  description2?: string;
  pListDate?: string | null;
  numOfOrderLines?: number;
  weight?: number;
  volum?: number;
  priority: number;
  pListStatus: number;
  operatorID?: string;
  operatorGroupID?: string;
  lockedBy?: string;
  lockStatus: number;
  startWorkDateTime?: string | null;
  stopWorkDateTime?: string | null;
  deliveryDate?: string | null;
  statusText: string;

  // Delivery info
  totalVolume?: number;
  totalWeight?: number;
  numOfPackages?: number;
  numOfPallets?: number;
  carrierName?: string;
  packZoneID?: string;
  packageRemark?: string;
  numOfOrderLinesPicked?: number;

  // Shipping labels
  shippingLabelsInitiated?: boolean;
  shippingLabelsPrinted?: boolean;

  // Signatures
  signature1?: string;
  signature1Location?: string;
  signature1DateTime?: string | null;
  signature2?: string;
  signature2Location?: string;
  signature2DateTime?: string | null;

  // Batch picking
  pListIsBatchYesNo?: boolean;
  packagingTypeNo?: number;
  filterGroupID?: string | null;
  pListNumOfPickZoneSplits?: number;

  // Delivery method
  deliveryMethodNo?: number;

  // Client and session
  clientID?: string;
  sessionID?: string;
  internalStatus?: number;
  externalSystemID?: number;

  // Configuration
  allowAutoComplete?: boolean;
  pListFinalizationLayout?: number;
  approvePackageStatus?: number;
  pListConfirmation?: number;
  qtyDeviationControlTerminated?: boolean;

  // Free fields
  pListFreeField1?: string;
  pListFreeField2?: string;
  pListFreeField3?: string;
  pListFreeField4?: string;
  pListFreeField5?: string;
  pListFreeField6?: string;
  pListFreeField7?: string;
  pListFreeField8?: string;
  pListFreeField9?: string;
  pListFreeField10?: string;

  // Audit fields
  createdBy?: string;
  created?: string | null;
  updatedBy?: string;
  lastUpdate?: string | null;

  // Comma-separated ERP order references (e.g. "1125711, 1125712"). Populated by
  // JOIN against Orders via PListOrders. Operator-facing UIs prefer this over pListID.
  orderNoERPs?: string | null;

  // Comma-separated customer names (DelCustomerName, fallback CustomerName) for orders
  // linked to this PList. Shown when the `picking.showCustomerName` setting is enabled.
  customerNames?: string | null;
}

export interface PListLineDto {
  id: number;
  companyNo: string;
  pListID: number;
  pListLineNo: number;
  orderNo: number;
  lineNo: number;
  itemNo?: string;
  itemBarCode?: string;
  itemDescription?: string;
  qty: number;
  qtyReserved: number;
  qtyPicked: number;
  binCode?: string;
  // Human-readable bin description (StcBin.Description1) for display.
  // Barcode validation still uses binCode.
  binDescription1?: string;
  warehouseID: string;
  lineStatus: number;
  sorting: number;
  isCompleted: boolean;
  remainingQty: number;
  // Order information
  orderNoERP?: string;
  customerName?: string;
  // Package information (for batch picking)
  packageNo?: number;
  visiblePackageNo?: number;
  // Serial/Batch tracking flags (from OrderLine)
  serialNoItemYesNo?: boolean;
  batchItemYesNo?: boolean;
  // Item type (0=Standard, 1=Batch, 2=Serienummer, 6=Serienummer i lagerstyring)
  itemType?: number;
  // Predefined serial number from ERP (for ItemType=6)
  requestedSerialNo?: string;
  // Expected batch from rule engine (for ItemType=4/5)
  requestedBatchNo?: string;
  requestedExpiryDate?: string;
  // Registered serial and batch numbers
  serialNumbers?: OrderSerialNoDto[];
  batchNumbers?: OrderBatchNoDto[];

  // OrderLine fields for dynamic extra info display (configured via UserProfile)
  orderLineFreeField1?: string;
  orderLineFreeField2?: string;
  orderLineFreeField3?: string;
  orderLineFreeField4?: string;
  orderLineFreeField5?: string;
  orderLineFreeField6?: string;
  orderLineFreeField7?: string;
  orderLineFreeField8?: string;
  orderLineFreeField9?: string;
  orderLineFreeField10?: string;
  orderLineDescription1?: string;
  orderLineOperatorTxt?: string;
  orderLineBatchID?: string;
  orderLineBuyersItemNo?: string;
  orderLineItemGroup1?: string;
  orderLineItemGroup2?: string;
  orderLineItemGroup3?: string;
  orderLineItemTemperatureInfo?: string;
  orderLineUNCode?: string;
  orderLineMemo1?: string;
}

// Serial Number Registration Types
export interface OrderSerialNoDto {
  id: number;
  pListID: number;
  orderNo: number;
  lineNo: number;
  serialNo: string;
  created?: string | null;
  createdBy?: string;
}

export interface CreateOrderSerialNoRequest {
  pListID: number;
  orderNo: number;
  lineNo: number;
  serialNo: string;
}

// Batch Number Registration Types
export interface OrderBatchNoDto {
  id: number;
  pListID: number;
  orderNo: number;
  lineNo: number;
  pListLineNo: number;
  batchNo: string;
  quantity: number;
  quantityGross?: number;
  created?: string | null;
  createdBy?: string;
}

export interface CreateOrderBatchNoRequest {
  pListID: number;
  orderNo: number;
  lineNo: number;
  pListLineNo: number;
  batchNo: string;
  quantity: number;
  quantityGross?: number;
}

export interface UpdateOrderBatchNoRequest {
  quantity: number;
  quantityGross?: number;
}

export interface PListDetailsDto {
  header: PListDto;
  lines: PListLineDto[];
  completedLines: number;
  totalLines: number;
  completionPercentage: number;
  warnings?: string[];
}


// OrderSSCC (Pallet Management) Types
export interface OrderSSCCDto {
  id: number;
  companyNo: string;
  pListID: number;
  orderNo: number;
  lineNo: number;
  sscc: string;
  ssccParent?: string;
  gtin?: string;
  quantity: number;
  ssccLevel: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  stackingWeight?: number;
  grossWeight?: number;
  locationName?: string;
  updatedBy?: string;
  lastUpdate?: string | null;
  createdBy?: string;
  created?: string | null;
}

export interface CreateOrderSSCCRequest {
  companyNo: string;
  pListID: number;
  orderNo: number;
  lineNo: number;
  sscc: string;
  ssccParent?: string;
  gtin?: string;
  quantity: number;
  ssccLevel: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  stackingWeight?: number;
  grossWeight?: number;
  locationName?: string;
  createdBy?: string;
  created?: string | null;
}

export interface PalletSummaryDto {
  sscc: string;
  ssccParent?: string;
  ssccLevel: number;
  totalItems: number;
  totalQuantity: number;
  totalWeight: number;
  isActive: boolean;
  created?: string | null;
  createdBy?: string;
  items: PalletItemDto[];
}

export interface PalletItemDto {
  orderNo: number;
  lineNo: number;
  gtin?: string;
  quantity: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  grossWeight?: number;
  locationName?: string;
}

// Discriminated Union Types for Pallet Handling
export interface EmptyPallet {
  type: 'empty';
  sscc: string;
  ssccLevel: number;
  created: string; // ISO timestamp
  createdBy: string;
  items: [];
  totalItems: 0;
  totalQuantity: 0;
  totalWeight: 0;
}

export interface DatabasePallet extends Omit<PalletSummaryDto, 'isActive'> {
  type: 'database';
}

export type UnifiedPallet = EmptyPallet | DatabasePallet;

// Type guard functions
export function isEmptyPallet(pallet: UnifiedPallet): pallet is EmptyPallet {
  return pallet.type === 'empty';
}

export function isDatabasePallet(pallet: UnifiedPallet): pallet is DatabasePallet {
  return pallet.type === 'database';
}

// Delete Picking List Result
export interface DeletePickingListResult {
  success: boolean;
  message: string;
  pListID: number;
  deletedLinesCount: number;
}

// Additional Items (Tilleggsvarer) Types
export interface AdditionalItemDto {
  itemNo: string;
  description1?: string;
  itemBarCode?: string;
  supplierItemNo?: string;
  itemUnitCode?: string;
  itemUnitTxt?: string;
  itemType?: number; // 0=Normal, 1=Batch, 2=Serial, 3=Special
  stdLocationName?: string;
  stdBinCode?: string;
  itemPictureName?: string;
}

export interface AddAdditionalLineRequest {
  itemNo: string;
  quantity: number;
  binCode?: string;
}

export interface AddAdditionalLineResponse {
  success: boolean;
  message: string;
  pListLineNo: number;
  orderNo: number;
  lineNo: number;
  itemNo: string;
  quantity: number;
  binCode?: string;
  warehouseID?: string;
}

// Aggregated Items Summary (for "samleplukk" overview)
export interface AggregatedPickingItemDto {
  itemNo: string;
  description?: string;
  itemBarCode?: string;
  unit?: string;
  remainingQty: number;
  numberOfPickingLists: number;
  stdLocationName?: string;
}

// SSCC Validation
export interface SSCCValidationResult {
  isValid: boolean;
  ssccRequired: boolean;
  message?: string;
  orders: SSCCValidationOrderDetail[];
}

export interface SSCCValidationOrderDetail {
  orderNo: number;
  orderNoERP?: string;
  ssccRequired: boolean;
  hasSSCCRecords: boolean;
}

// Operator Batch Picking
export interface ValidateOperatorBatchRequest {
  pListIds: number[];
}

export interface ValidateOperatorBatchResponse {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  totalLines: number;
  totalOrders: number;
  totalWeight: number;
  totalVolume: number;
  highestPriority: number;
  sourcePLists: OperatorBatchPListSummary[];
  maxPListsInBatch: number;
}

export interface OperatorBatchPListSummary {
  pListID: number;
  description1?: string;
  description2?: string;
  numOfOrderLines?: number;
  weight?: number;
  volum?: number;
  priority: number;
  carrierName?: string;
  warehouseID?: string;
  deliveryMethodNo?: number;
  packZoneID?: string;
  deliveryDate?: string;
}

export interface CreateOperatorBatchRequest {
  pListIds: number[];
}

export interface CreateOperatorBatchResponse {
  success: boolean;
  newPListId: number;
  errorMessage?: string;
  sourcePListCount: number;
  totalLines: number;
  totalPackages: number;
}
